import React, { useState } from 'react';
import { Copy, Check, Terminal, Webhook, Key, BookOpen, ArrowRight, Code2, Zap } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { Button } from '../components/ui/button';

const SNIPPETS = {
  curl: `curl -X POST https://api.coredesk.com.br/v1/messages \\
  -H "Authorization: Bearer $CORE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "channel": "whatsapp",
    "to": "+5511900000000",
    "text": "Olá! Sua compra #10442 foi confirmada."
  }'`,
  node: `import { CoreDesk } from '@coredesk/sdk';

const core = new CoreDesk({ apiKey: process.env.CORE_API_KEY });

await core.messages.send({
  channel: 'whatsapp',
  to: '+5511900000000',
  text: 'Olá! Sua compra foi confirmada.',
});`,
  python: `from coredesk import CoreDesk

core = CoreDesk(api_key=os.environ["CORE_API_KEY"])

core.messages.send(
    channel="whatsapp",
    to="+5511900000000",
    text="Olá! Sua compra foi confirmada.",
)`,
};

const features = [
  { icon: Terminal, title: 'REST API', desc: 'Endpoints RESTful para mensagens, contatos, tickets e mais.' },
  { icon: Webhook, title: 'Webhooks', desc: 'Receba eventos em tempo real: nova conversa, ticket, resposta e etc.' },
  { icon: Zap, title: 'Rate limits generosos', desc: '1.000 req/min por chave, com aumento sob demanda para Enterprise.' },
  { icon: Key, title: 'Autenticação segura', desc: 'API keys por ambiente, com escopos granulares e auditoria completa.' },
];

const Desenvolvedores = () => {
  const [tab, setTab] = useState('curl');
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const copy = () => {
    navigator.clipboard.writeText(SNIPPETS[tab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <Seo
        title="API e Desenvolvedores"
        description="API REST completa, webhooks em tempo real e SDKs em Node.js e Python. Construa qualquer integração sobre a CoreDesk com documentação clara e sandbox grátis."
        path="/desenvolvedores"
      />
      <Header />
      <PageHero
        eyebrow="API & Desenvolvedores"
        title="Construa qualquer coisa |sobre a CoreDesk"
        subtitle="API REST completa, webhooks em tempo real, SDKs oficiais em Node.js e Python. Documentação clara e sandbox gratuita."
      />

      {/* Code sample */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#FF5B24]">
                <Code2 size={13} /> Começando rápido
              </div>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a0e27] leading-tight">
                Envie sua primeira mensagem em <span className="text-[#FF5B24]">menos de 5 minutos</span>
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                A API da CoreDesk foi pensada para desenvolvedores que gostam de simplicidade. Autentique com uma chave, escolha um canal e envie.
              </p>
              <div className="mt-6 flex gap-3">
                <Button asChild className="btn-coral rounded-full h-11 px-5 text-sm font-semibold">
                  <Link to="/desenvolvedores/docs">Ler documentação <ArrowRight size={15} className="ml-1" /></Link>
                </Button>
                <Button variant="outline" onClick={() => navigate('/contato')} className="rounded-full h-11 px-5 text-sm font-semibold">
                  Solicitar sandbox
                </Button>
              </div>
            </div>
            <div className="rounded-2xl bg-[#0a0e27] overflow-hidden shadow-2xl">
              <div className="flex items-center gap-1 px-4 py-3 border-b border-white/10">
                <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                <div className="ml-auto flex items-center gap-1">
                  {['curl', 'node', 'python'].map((t) => (
                    <button key={t} onClick={() => setTab(t)} className={`px-3 py-1 rounded-md text-xs font-semibold ${tab === t ? 'bg-white/10 text-white' : 'text-white/50 hover:text-white'}`}>
                      {t === 'curl' ? 'cURL' : t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                  <button onClick={copy} className="ml-2 p-1.5 rounded-md hover:bg-white/10 text-white/70" aria-label="Copiar">
                    {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
              <pre className="px-6 py-6 text-xs md:text-sm text-white/90 leading-relaxed overflow-x-auto">
                <code>{SNIPPETS[tab]}</code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-[#FAF6F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a0e27]">Tudo que você precisa para escalar</h2>
            <p className="mt-3 text-slate-600">Feito por engenheiros, para engenheiros. Sem surpresas, sem gambiarras.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition">
                <div className="w-11 h-11 rounded-xl bg-[#FFF1EA] flex items-center justify-center">
                  <f.icon size={20} className="text-[#FF5B24]" />
                </div>
                <h3 className="mt-4 font-extrabold text-[#0a0e27]">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-[#0a0e27] mb-8">Recursos técnicos</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: 'Referência da API', desc: 'Todos os endpoints, parâmetros e respostas.', icon: BookOpen, to: '/desenvolvedores/docs#referencia', cta: 'Acessar' },
              { title: 'Guias e tutoriais', desc: 'Passo a passo para casos de uso comuns.', icon: Code2, to: '/desenvolvedores/docs#guias', cta: 'Acessar' },
              { title: 'Postman Collection', desc: 'Importe e teste todos os endpoints em segundos.', icon: Terminal, href: '/coredesk.postman_collection.json', download: true, cta: 'Baixar' },
            ].map((r) => {
              const inner = (
                <>
                  <r.icon size={22} className="text-[#FF5B24]" />
                  <h3 className="mt-4 font-extrabold text-[#0a0e27] group-hover:text-[#FF5B24] transition">{r.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.desc}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0a0e27]">
                    {r.cta} <ArrowRight size={13} />
                  </div>
                </>
              );
              const cls = 'group block bg-white rounded-2xl border border-slate-100 p-6 hover:border-[#FF5B24]/40 hover:shadow-xl transition-all';
              return r.download ? (
                <a key={r.title} href={r.href} download className={cls}>{inner}</a>
              ) : (
                <Link key={r.title} to={r.to} className={cls}>{inner}</Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Desenvolvedores;
